import { useState, useRef, useEffect, useMemo } from 'react';
import { useBeforeAfterSliderInteraction } from '../hooks/useBeforeAfterSliderInteraction';

function fitAspectRatioWithinContainer({
  containerWidth,
  containerHeight,
  imageWidth,
  imageHeight,
}) {
  if (!containerWidth || !containerHeight || !imageWidth || !imageHeight) {
    return { width: containerWidth || 0, height: containerHeight || 0, left: 0, top: 0 };
  }

  const imageAspect = imageWidth / imageHeight;
  const containerAspect = containerWidth / containerHeight;

  if (imageAspect > containerAspect) {
    const width = containerWidth;
    const height = width / imageAspect;
    return { width, height, left: 0, top: (containerHeight - height) / 2 };
  }

  const height = containerHeight;
  const width = height * imageAspect;
  return { width, height, left: (containerWidth - width) / 2, top: 0 };
}

function BeforeAfterSlider({
  before,
  after,
  onPositionChange,
  initialPosition = 25,
  imageFit = 'cover',
  loading,
  decoding,
  onUserInteraction,
  onBeforeImageLoad,
  interactionMode = 'hover',
}) {
  const [position, setPosition] = useState(initialPosition);
  const [beforeError, setBeforeError] = useState(false);
  const [afterError, setAfterError] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const handleRef = useRef(null);

  const isDragMode = interactionMode === 'drag';
  const isContain = imageFit === 'contain';
  const interactionBoundsRef = isContain ? stageRef : containerRef;

  const { isSliding, containerHandlers, handleDragHandlers, handleA11yProps } =
    useBeforeAfterSliderInteraction({
      containerRef: interactionBoundsRef,
      handleRef,
      position,
      setPosition,
      onUserInteraction,
      interactionMode,
    });

  useEffect(() => {
    setBeforeError(false);
    setAfterError(false);
  }, [before, after]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || !isContain || typeof ResizeObserver === 'undefined') return undefined;

    const updateSize = () => {
      setContainerSize({
        width: node.clientWidth,
        height: node.clientHeight,
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isContain]);

  useEffect(() => {
    onPositionChange?.(position);
  }, [position, onPositionChange]);

  const imgDecoding = decoding ?? (loading ? 'async' : undefined);
  const fittedRect = useMemo(
    () =>
      isContain
        ? fitAspectRatioWithinContainer({
            containerWidth: containerSize.width,
            containerHeight: containerSize.height,
            imageWidth: imageSize.width,
            imageHeight: imageSize.height,
          })
        : null,
    [containerSize.height, containerSize.width, imageSize.height, imageSize.width, isContain],
  );
  const stageStyle = fittedRect
    ? {
        width: `${fittedRect.width}px`,
        height: `${fittedRect.height}px`,
        left: `${fittedRect.left}px`,
        top: `${fittedRect.top}px`,
      }
    : undefined;

  if (beforeError || afterError) {
    return (
      <div className="rt-slider-wrap rt-slider-error" role="alert">
        Could not load comparison images. Check your connection or try again later.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`rt-slider-wrap${isContain ? ' rt-slider-wrap--contain' : ''}${
        isDragMode ? ' rt-slider-wrap--drag' : ''
      }`}
      {...containerHandlers}
    >
      <div ref={stageRef} className="rt-slider-stage" style={stageStyle}>
        <img
          src={before}
          alt="Before room redesign"
          className="rt-slider-img rt-slider-img--before"
          draggable={false}
          {...(loading ? { loading } : {})}
          {...(imgDecoding ? { decoding: imgDecoding } : {})}
          onError={() => setBeforeError(true)}
          onLoad={(event) => {
            setImageSize({
              width: event.currentTarget.naturalWidth,
              height: event.currentTarget.naturalHeight,
            });
            onBeforeImageLoad?.(event.currentTarget);
          }}
        />
        <div
          className="rt-slider-before-mask"
          style={{
            position: 'absolute',
            inset: 0,
            right: `${100 - position}%`,
            background: 'rgba(0,0,0,0.45)',
            pointerEvents: 'none',
            opacity: isSliding ? 0 : 1,
            transition: 'opacity 0.4s ease',
            zIndex: 1,
          }}
        />
        <div
          className="rt-slider-after-wrap"
          style={{ clipPath: `inset(0 0 0 ${position}%)` }}
        >
          <img
            src={after}
            alt="After room redesign"
            className="rt-slider-img rt-slider-img--after"
            draggable={false}
            {...(loading ? { loading } : {})}
            {...(imgDecoding ? { decoding: imgDecoding } : {})}
            onError={() => setAfterError(true)}
          />
        </div>
        <div
          ref={handleRef}
          className="rt-slider-handle"
          style={{ left: `${position}%` }}
          {...handleA11yProps}
          {...handleDragHandlers}
        >
          <div className="rt-slider-grip">
            <span className="rt-slider-grip-arrow rt-slider-grip-arrow--left">&#8592;</span>
            <span className="rt-slider-grip-arrow rt-slider-grip-arrow--right">&#8594;</span>
          </div>
        </div>
        <span className="rt-slider-label rt-slider-label--before">Before</span>
        <span className="rt-slider-label rt-slider-label--after">After</span>
      </div>
    </div>
  );
}

export default BeforeAfterSlider;
